import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  type Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/client";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Download,
  Filter,
  Inbox,
  ListChecks,
  Loader2,
  MessageSquare,
  RefreshCw,
  Search,
  Trash2,
  Wrench,
  XCircle,
  Zap,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

type TicketStatus = "open" | "in_review" | "resolved";
type TicketPriority = "low" | "medium" | "high" | "critical";
type ProblemStatus = "pending" | "fixing" | "resolved";

type ChecklistEntry = {
  status: "ok" | "problem" | "not_tested";
  rating?: string;
  comment?: string;
};

type Problem = {
  id: string;
  title: string;
  sourceChecklistItem: string;
  score: number | null;
  userComment: string;
  status: ProblemStatus;
  resolved: boolean;
  actionTaken: string;
  owner: string;
  updatedAt: string | null;
};

type AttachmentItem = {
  name: string;
  url: string;
  mimeType: string;
  size?: number;
  kind?: string;
};

type SupportTicket = {
  id: string;
  uid: string | null;
  userEmail: string | null;
  userName: string | null;
  category: string | null;
  relatedModule: string | null;
  message: string;
  platform: string | null;
  appVersion: string | null;
  source: "web" | "mobile" | null;
  status: TicketStatus;
  priority: TicketPriority | null;
  internalNote: string | null;
  checklistBeta: Record<string, ChecklistEntry> | null;
  notaGeral: string | null;
  recomendaria: string | null;
  principalProblema: string | null;
  principalMelhoria: string | null;
  problems: Problem[] | null;
  createdAt: Timestamp | null;
  // web-specific
  kind: string | null;
  title: string | null;
  description: string | null;
  returnEmail: string | null;
  moduleAffected: string | null;
  clinicalErrorType: string | null;
  clinicalImpact: string | null;
  impact: string | null;
  expectedFlow: string | null;
  attachments: AttachmentItem[] | null;
  context: Record<string, string> | null;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: { value: TicketStatus | "all"; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "open", label: "Aberto" },
  { value: "in_review", label: "Em análise" },
  { value: "resolved", label: "Resolvido" },
];

const PRIORITY_OPTIONS: { value: TicketPriority | "all"; label: string }[] = [
  { value: "all", label: "Todas" },
  { value: "critical", label: "Crítica" },
  { value: "high", label: "Alta" },
  { value: "medium", label: "Média" },
  { value: "low", label: "Baixa" },
];

const CATEGORY_OPTIONS = [
  { value: "all", label: "Todas" },
  { value: "assistance", label: "Assistência" },
  { value: "complaint", label: "Reclamação" },
  { value: "suggestion", label: "Sugestão" },
];

const MODULE_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "patients", label: "Pacientes" },
  { value: "surgical_plan", label: "Plano Cirúrgico" },
  { value: "report_analysis", label: "Análise de Laudos" },
  { value: "guidelines", label: "Diretrizes" },
  { value: "ortho_id", label: "Ortho ID" },
  { value: "login_register", label: "Login / Cadastro" },
  { value: "other", label: "Outro" },
];

const PLATFORM_OPTIONS = [
  { value: "all", label: "Todas" },
  { value: "ios", label: "iOS" },
  { value: "android", label: "Android" },
];

const SOURCE_OPTIONS = [
  { value: "all", label: "Todas origens" },
  { value: "web", label: "Web" },
  { value: "mobile", label: "Mobile" },
];

const CHECKLIST_LABELS: Record<string, string> = {
  login: "Login e acesso",
  patientRegister: "Cadastro de paciente",
  upload: "Upload de exame",
  viewExam: "Visualização do exame",
  surgicalPlan: "Plano Cirúrgico",
  reportAnalysis: "Análise de Laudo",
  guidelines: "Diretrizes",
  orthoId: "Ortho ID / Banner / PDF",
  aiSpeed: "Velocidade da IA",
  usability: "Facilidade de uso",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(ts: Timestamp | null): string {
  if (!ts) return "—";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(ts.toDate());
  } catch { return "—"; }
}

function categoryLabel(v: string | null): string {
  return CATEGORY_OPTIONS.find((c) => c.value === v)?.label ?? v ?? "—";
}

function moduleLabel(v: string | null): string {
  return MODULE_OPTIONS.find((m) => m.value === v)?.label ?? v ?? "—";
}

function recommendLabel(v: string | null): string {
  if (v === "yes") return "Sim";
  if (v === "no") return "Não";
  if (v === "maybe") return "Talvez";
  return v ?? "—";
}

/** Derive Problem list from checklistBeta.
 *  Triggers when: status === "problem" OR (score < 7 AND comment filled) */
function deriveProblems(ticket: SupportTicket): Problem[] {
  if (!ticket.checklistBeta) return [];
  const result: Problem[] = [];
  for (const [key, entry] of Object.entries(ticket.checklistBeta)) {
    const isProblem = entry.status === "problem";
    const score = entry.rating ? parseInt(entry.rating, 10) : null;
    const lowScore = score !== null && !isNaN(score) && score < 7;
    const hasComment = Boolean(entry.comment?.trim());
    if (isProblem || (lowScore && hasComment)) {
      result.push({
        id: key,
        title: CHECKLIST_LABELS[key] ?? key,
        sourceChecklistItem: key,
        score: score,
        userComment: entry.comment?.trim() ?? "",
        status: "pending",
        resolved: false,
        actionTaken: "",
        owner: "",
        updatedAt: null,
      });
    }
  }
  return result;
}

// ─── Badges ───────────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: TicketStatus }> = ({ status }) => {
  const map: Record<TicketStatus, { label: string; cls: string }> = {
    open: { label: "Aberto", cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/25" },
    in_review: { label: "Em análise", cls: "bg-amber-500/15 text-amber-300 border-amber-500/25" },
    resolved: { label: "Resolvido", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25" },
  };
  const { label, cls } = map[status] ?? map.open;
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${cls}`}>
      {label}
    </span>
  );
};

const PriorityBadge: React.FC<{ priority: TicketPriority | null }> = ({ priority }) => {
  if (!priority) return <span className="text-slate-500 text-xs">—</span>;
  const map: Record<TicketPriority, { label: string; cls: string }> = {
    low: { label: "Baixa", cls: "bg-slate-500/15 text-slate-400 border-slate-500/20" },
    medium: { label: "Média", cls: "bg-blue-500/15 text-blue-300 border-blue-500/25" },
    high: { label: "Alta", cls: "bg-orange-500/15 text-orange-300 border-orange-500/25" },
    critical: { label: "Crítica", cls: "bg-red-500/15 text-red-300 border-red-500/25" },
  };
  const { label, cls } = map[priority];
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${cls}`}>
      {label}
    </span>
  );
};

const ProblemStatusBadge: React.FC<{ status: ProblemStatus }> = ({ status }) => {
  const map: Record<ProblemStatus, { label: string; cls: string }> = {
    pending: { label: "Pendente", cls: "bg-red-500/15 text-red-300 border-red-500/20" },
    fixing: { label: "Em correção", cls: "bg-amber-500/15 text-amber-300 border-amber-500/20" },
    resolved: { label: "Resolvido", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/20" },
  };
  const { label, cls } = map[status];
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cls}`}>
      {label}
    </span>
  );
};

// ─── Counter Card ─────────────────────────────────────────────────────────────

const CounterCard: React.FC<{
  label: string; count: number; icon: React.ReactNode; accent: string;
}> = ({ label, count, icon, accent }) => (
  <div className={`flex items-center justify-between rounded-2xl border bg-slate-900/80 px-5 py-4 ${accent}`}>
    <div>
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
      <p className="mt-1 text-3xl font-black text-white">{count}</p>
    </div>
    <div className="opacity-80">{icon}</div>
  </div>
);

// ─── Problem Row ──────────────────────────────────────────────────────────────

const ProblemRow: React.FC<{
  problem: Problem;
  onChange: (updated: Problem) => void;
}> = ({ problem, onChange }) => {
  function update(patch: Partial<Problem>) {
    onChange({ ...problem, ...patch, updatedAt: new Date().toISOString() });
  }

  return (
    <div className={`rounded-xl border px-4 py-3 space-y-3 transition-all ${
      problem.resolved
        ? "bg-emerald-500/5 border-emerald-500/15"
        : problem.status === "fixing"
          ? "bg-amber-500/5 border-amber-500/15"
          : "bg-red-500/5 border-red-500/15"
    }`}>
      {/* Header row */}
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button
          onClick={() => update({ resolved: !problem.resolved, status: !problem.resolved ? "resolved" : "pending" })}
          className={`shrink-0 mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
            problem.resolved
              ? "bg-emerald-500 border-emerald-500"
              : "border-slate-600 hover:border-emerald-400"
          }`}
          title="Marcar como resolvido"
        >
          {problem.resolved && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`text-sm font-bold ${problem.resolved ? "text-slate-400 line-through" : "text-slate-100"}`}>
              {problem.title}
            </span>
            <ProblemStatusBadge status={problem.status} />
            {problem.score !== null && (
              <span className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${
                problem.score < 5
                  ? "bg-red-500/20 text-red-300"
                  : problem.score < 7
                    ? "bg-amber-500/20 text-amber-300"
                    : "bg-slate-700 text-slate-300"
              }`}>
                Nota {problem.score}/10
              </span>
            )}
          </div>
          {problem.userComment && (
            <p className="text-[12px] text-slate-400 italic">"{problem.userComment}"</p>
          )}
        </div>
      </div>

      {/* Edit controls — hidden when resolved */}
      {!problem.resolved && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pl-8">
          <div>
            <label className="block text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">Status</label>
            <select
              value={problem.status}
              onChange={(e) => update({ status: e.target.value as ProblemStatus })}
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-cyan-500/50"
            >
              <option value="pending">Pendente</option>
              <option value="fixing">Em correção</option>
              <option value="resolved">Resolvido</option>
            </select>
          </div>
          <div>
            <label className="block text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">Responsável</label>
            <input
              type="text"
              value={problem.owner}
              onChange={(e) => update({ owner: e.target.value })}
              placeholder="Nome / equipe"
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-cyan-500/50 placeholder:text-slate-600"
            />
          </div>
          <div className="sm:col-span-1">
            <label className="block text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">Ação tomada</label>
            <input
              type="text"
              value={problem.actionTaken}
              onChange={(e) => update({ actionTaken: e.target.value })}
              placeholder="Descreva a correção..."
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-cyan-500/50 placeholder:text-slate-600"
            />
          </div>
        </div>
      )}

      {/* Resolved summary */}
      {problem.resolved && (problem.actionTaken || problem.owner) && (
        <div className="pl-8 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-500">
          {problem.owner && <span>Responsável: <span className="text-slate-300">{problem.owner}</span></span>}
          {problem.actionTaken && <span>Ação: <span className="text-slate-300">{problem.actionTaken}</span></span>}
        </div>
      )}
    </div>
  );
};

// ─── Ticket Row ───────────────────────────────────────────────────────────────

const TicketRow: React.FC<{ ticket: SupportTicket; onUpdated: () => void }> = ({ ticket, onUpdated }) => {
  const [expanded, setExpanded] = useState(false);
  const [editStatus, setEditStatus] = useState<TicketStatus>(ticket.status);
  const [editPriority, setEditPriority] = useState<TicketPriority | "">(ticket.priority ?? "");
  const [editNote, setEditNote] = useState(ticket.internalNote ?? "");
  const [problems, setProblems] = useState<Problem[]>(() =>
    ticket.problems?.length ? ticket.problems : deriveProblems(ticket)
  );
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Keep problems in sync when ticket changes from Firestore
  const ticketProblemsKey = JSON.stringify(ticket.problems);
  useEffect(() => {
    setProblems(ticket.problems?.length ? ticket.problems : deriveProblems(ticket));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketProblemsKey]);

  const allProblemsResolved = problems.length > 0 && problems.every((p) => p.resolved);
  const pendingCount = problems.filter((p) => p.status === "pending" && !p.resolved).length;
  const fixingCount = problems.filter((p) => p.status === "fixing").length;

  const isDirty =
    editStatus !== ticket.status ||
    editPriority !== (ticket.priority ?? "") ||
    editNote !== (ticket.internalNote ?? "") ||
    JSON.stringify(problems) !== JSON.stringify(ticket.problems?.length ? ticket.problems : deriveProblems(ticket));

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "supportTickets", ticket.id));
      onUpdated();
    } catch (err) {
      console.error("[AdminSupport][delete:error]", err);
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setSaveMsg("");
    try {
      await updateDoc(doc(db, "supportTickets", ticket.id), {
        status: editStatus,
        priority: editPriority || null,
        internalNote: editNote.trim() || null,
        problems,
        updatedAt: serverTimestamp(),
      });
      setSaveMsg("Salvo ✓");
      onUpdated();
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (err) {
      console.error("[AdminSupport][save:error]", err);
      setSaveMsg("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  function updateProblem(updated: Problem) {
    setProblems((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  }

  const hasOverallFeedback =
    ticket.notaGeral || ticket.recomendaria || ticket.principalProblema || ticket.principalMelhoria;

  const hasChecklist =
    ticket.checklistBeta &&
    Object.values(ticket.checklistBeta).some((e) => e.status !== "not_tested");

  return (
    <div className="rounded-2xl border border-slate-800/70 bg-slate-900/60 overflow-hidden">
      {/* ─ Header ─ */}
      <div className="flex items-start gap-3 px-5 py-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <StatusBadge status={editStatus} />
            <PriorityBadge priority={editPriority as TicketPriority | null} />
            {problems.length > 0 && (
              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${
                pendingCount > 0
                  ? "bg-red-500/10 border-red-500/20 text-red-300"
                  : "bg-emerald-500/10 border-emerald-500/20 text-emerald-300"
              }`}>
                <ListChecks className="w-3 h-3" />
                {pendingCount > 0 ? `${pendingCount} pendente${pendingCount > 1 ? "s" : ""}` : "Tudo resolvido"}
              </span>
            )}
            <span className="text-[11px] text-slate-500">{formatDate(ticket.createdAt)}</span>
            <span className={`text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 border ${
              ticket.source === "web"
                ? "bg-violet-500/15 border-violet-500/25 text-violet-300"
                : "bg-slate-700/60 border-slate-600/40 text-slate-400"
            }`}>
              {ticket.source === "web" ? "WEB" : "MOBILE"}
            </span>
            {ticket.platform && ticket.platform !== "web" && (
              <span className="text-[11px] uppercase tracking-wide text-slate-500 bg-slate-800 rounded-full px-2 py-0.5">
                {ticket.platform}{ticket.appVersion ? ` v${ticket.appVersion}` : ""}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[12px] text-slate-400 mb-2">
            <span>
              <span className="text-slate-500">Usuário:</span>{" "}
              <span className="text-slate-200 font-medium">{ticket.userEmail ?? ticket.uid ?? "—"}</span>
            </span>
            {ticket.userName && (
              <span><span className="text-slate-500">Nome:</span> <span className="text-slate-200">{ticket.userName}</span></span>
            )}
            <span><span className="text-slate-500">Categoria:</span> <span className="text-slate-200">{categoryLabel(ticket.category)}</span></span>
            <span><span className="text-slate-500">Módulo:</span> <span className="text-slate-200">{moduleLabel(ticket.relatedModule)}</span></span>
          </div>

          {ticket.title && (
            <p className="text-sm font-bold text-white mb-0.5">{ticket.title}</p>
          )}
          <p className="text-sm text-slate-100 leading-relaxed line-clamp-2">{ticket.message}</p>
        </div>

        <button
          onClick={() => setExpanded((v) => !v)}
          className="shrink-0 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* ─ Expanded ─ */}
      {expanded && (
        <div className="border-t border-slate-800/60 px-5 py-5 space-y-6">

          {/* ── PROBLEMAS IDENTIFICADOS ───────────────────────────── */}
          {problems.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-red-400" />
                  <p className="text-[13px] font-black text-white uppercase tracking-wide">
                    Problemas identificados
                  </p>
                  <span className="text-[11px] font-bold text-slate-400">
                    ({problems.filter((p) => p.resolved).length}/{problems.length} resolvidos)
                  </span>
                </div>
                {/* Mini counters */}
                <div className="flex gap-2">
                  {pendingCount > 0 && (
                    <span className="text-[10px] font-bold rounded-full bg-red-500/10 border border-red-500/20 text-red-300 px-2 py-0.5">
                      {pendingCount} pendente{pendingCount > 1 ? "s" : ""}
                    </span>
                  )}
                  {fixingCount > 0 && (
                    <span className="text-[10px] font-bold rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300 px-2 py-0.5">
                      {fixingCount} em correção
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                {problems.map((problem) => (
                  <ProblemRow key={problem.id} problem={problem} onChange={updateProblem} />
                ))}
              </div>

              {/* Suggest closing ticket */}
              {allProblemsResolved && editStatus !== "resolved" && (
                <div className="mt-3 flex items-center gap-3 rounded-xl border border-emerald-500/25 bg-emerald-500/8 px-4 py-3">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  <p className="text-sm text-emerald-300 flex-1">
                    Todos os problemas foram resolvidos. Deseja marcar o chamado como{" "}
                    <strong>Resolvido</strong>?
                  </p>
                  <button
                    onClick={() => setEditStatus("resolved")}
                    className="shrink-0 rounded-lg bg-emerald-600/80 hover:bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white transition-colors"
                  >
                    Sim, resolver
                  </button>
                </div>
              )}
            </div>
          )}

          {problems.length === 0 && (ticket.checklistBeta && Object.values(ticket.checklistBeta).some(e => e.status !== "not_tested")) && (
            <div className="flex items-center gap-2 rounded-xl border border-slate-700/40 bg-slate-800/30 px-4 py-3 text-sm text-slate-400">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              Nenhum problema crítico identificado no checklist.
            </div>
          )}

          {/* ── MENSAGEM COMPLETA ─────────────────────────────────── */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Mensagem completa</p>
            <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap bg-slate-800/50 rounded-xl px-4 py-3">
              {ticket.message}
            </p>
          </div>

          {/* ── CAMPOS ESPECÍFICOS DO WEB ────────────────────────── */}
          {ticket.source === "web" && (
            <div className="space-y-3">
              {/* Extra web fields */}
              {(ticket.impact || ticket.expectedFlow || ticket.clinicalErrorType || ticket.clinicalImpact || ticket.returnEmail) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {ticket.returnEmail && (
                    <div className="bg-slate-800/40 rounded-xl px-4 py-3">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">E-mail de retorno</p>
                      <p className="text-sm text-slate-200">{ticket.returnEmail}</p>
                    </div>
                  )}
                  {ticket.clinicalErrorType && (
                    <div className="bg-slate-800/40 rounded-xl px-4 py-3">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Tipo de erro clínico</p>
                      <p className="text-sm text-slate-200">{ticket.clinicalErrorType}</p>
                    </div>
                  )}
                  {ticket.impact && (
                    <div className="bg-slate-800/40 rounded-xl px-4 py-3 sm:col-span-2">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Impacto relatado</p>
                      <p className="text-sm text-slate-200">{ticket.impact}</p>
                    </div>
                  )}
                  {ticket.clinicalImpact && (
                    <div className="bg-slate-800/40 rounded-xl px-4 py-3 sm:col-span-2">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Impacto clínico</p>
                      <p className="text-sm text-slate-200">{ticket.clinicalImpact}</p>
                    </div>
                  )}
                  {ticket.expectedFlow && (
                    <div className="bg-slate-800/40 rounded-xl px-4 py-3 sm:col-span-2">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Fluxo esperado</p>
                      <p className="text-sm text-slate-200">{ticket.expectedFlow}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Attachments */}
              {ticket.attachments && ticket.attachments.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Anexos ({ticket.attachments.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {ticket.attachments.map((att, i) => (
                      <a
                        key={i}
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-xl border border-cyan-500/20 bg-cyan-500/8 hover:bg-cyan-500/15 px-3 py-2 text-xs font-semibold text-cyan-300 transition-colors"
                      >
                        {att.kind === "screenshot" ? "📸" : "📎"} {att.name}
                        {att.size ? <span className="text-slate-500">({(att.size / 1024).toFixed(0)} KB)</span> : null}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Browser context */}
              {ticket.context && Object.keys(ticket.context).length > 0 && (
                <details className="rounded-xl border border-slate-700/40 overflow-hidden">
                  <summary className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 cursor-pointer hover:bg-slate-800/30 transition-colors">
                    Contexto do navegador
                  </summary>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 px-4 pb-3 pt-1">
                    {Object.entries(ticket.context).map(([key, val]) => val ? (
                      <div key={key} className="bg-slate-800/40 rounded-lg px-3 py-2">
                        <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500">{key}</p>
                        <p className="text-[11px] text-slate-300 truncate">{val}</p>
                      </div>
                    ) : null)}
                  </div>
                </details>
              )}
            </div>
          )}

          {/* ── AVALIAÇÃO GERAL ───────────────────────────────────── */}
          {hasOverallFeedback && (
            <div className="grid grid-cols-2 gap-3">
              {ticket.notaGeral && (
                <div className="bg-slate-800/40 rounded-xl px-4 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Nota geral</p>
                  <p className="text-2xl font-black text-white">
                    {ticket.notaGeral}<span className="text-sm text-slate-400 font-normal">/10</span>
                  </p>
                </div>
              )}
              {ticket.recomendaria && (
                <div className="bg-slate-800/40 rounded-xl px-4 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Recomendaria</p>
                  <p className="text-sm font-bold text-white">{recommendLabel(ticket.recomendaria)}</p>
                </div>
              )}
              {ticket.principalProblema && (
                <div className="bg-slate-800/40 rounded-xl px-4 py-3 col-span-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Principal problema</p>
                  <p className="text-sm text-slate-200">{ticket.principalProblema}</p>
                </div>
              )}
              {ticket.principalMelhoria && (
                <div className="bg-slate-800/40 rounded-xl px-4 py-3 col-span-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Principal melhoria</p>
                  <p className="text-sm text-slate-200">{ticket.principalMelhoria}</p>
                </div>
              )}
            </div>
          )}

          {/* ── CHECKLIST BETA (itens OK) ─────────────────────────── */}
          {hasChecklist && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Checklist Beta — todos os itens</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {Object.entries(ticket.checklistBeta!).map(([key, entry]) => {
                  if (entry.status === "not_tested") return null;
                  return (
                    <div key={key} className={`flex items-start gap-2 rounded-xl px-3 py-2.5 ${
                      entry.status === "ok" ? "bg-emerald-500/5 border border-emerald-500/10" : "bg-red-500/5 border border-red-500/10"
                    }`}>
                      <span className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${entry.status === "ok" ? "bg-emerald-400" : "bg-red-400"}`} />
                      <div className="min-w-0">
                        <p className="text-[12px] font-semibold text-slate-200">{CHECKLIST_LABELS[key] ?? key}</p>
                        <p className="text-[11px] text-slate-500">
                          {entry.status === "ok" ? "OK" : "Problema"}
                          {entry.rating ? ` · Nota ${entry.rating}/10` : ""}
                        </p>
                        {entry.comment && <p className="text-[11px] text-slate-400 mt-0.5">{entry.comment}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── GERENCIAR CHAMADO ─────────────────────────────────── */}
          <div className="border-t border-slate-800/60 pt-4 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Gerenciar chamado</p>
            <div className="flex flex-wrap gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Status</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as TicketStatus)}
                  className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-xl px-3 py-2 focus:outline-none focus:border-cyan-500/50"
                >
                  <option value="open">Aberto</option>
                  <option value="in_review">Em análise</option>
                  <option value="resolved">Resolvido</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Prioridade</label>
                <select
                  value={editPriority}
                  onChange={(e) => setEditPriority(e.target.value as TicketPriority | "")}
                  className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-xl px-3 py-2 focus:outline-none focus:border-cyan-500/50"
                >
                  <option value="">Sem prioridade</option>
                  <option value="low">Baixa</option>
                  <option value="medium">Média</option>
                  <option value="high">Alta</option>
                  <option value="critical">Crítica</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Nota interna</label>
              <textarea
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                rows={2}
                placeholder="Observação interna (não visível ao usuário)..."
                className="w-full bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-xl px-3 py-2 resize-none focus:outline-none focus:border-cyan-500/50 placeholder:text-slate-600"
              />
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSave}
                  disabled={!isDirty || saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-cyan-600/90 hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm font-bold text-white transition-colors"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Salvar alterações
                </button>
                {saveMsg && (
                  <span className={`text-sm font-semibold ${saveMsg.startsWith("Erro") ? "text-red-400" : "text-emerald-400"}`}>
                    {saveMsg}
                  </span>
                )}
              </div>

              {/* Delete */}
              {!confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="inline-flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/8 hover:bg-red-500/15 px-3 py-2 text-sm font-bold text-red-400 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir chamado
                </button>
              ) : (
                <div className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2">
                  <span className="text-sm text-red-300 font-semibold">Confirmar exclusão?</span>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 hover:bg-red-500 disabled:opacity-50 px-3 py-1 text-xs font-black text-white transition-colors"
                  >
                    {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    Excluir
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleting}
                    className="rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 px-3 py-1 text-xs font-bold text-slate-300 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── CSV Export ───────────────────────────────────────────────────────────────

function exportCSV(tickets: SupportTicket[]) {
  const headers = [
    "ID", "Data", "Email", "Nome", "Categoria", "Módulo", "Status", "Prioridade",
    "Plataforma", "Versão", "Mensagem", "NotaGeral", "Recomendaria",
    "PrincipalProblema", "PrincipalMelhoria", "NotaInterna",
    "Problemas_Pendentes", "Problemas_Total",
  ];
  const escape = (v: string | null | undefined) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const rows = tickets.map((t) => {
    const derived = t.problems?.length ? t.problems : deriveProblems(t);
    return [
      escape(t.id), escape(formatDate(t.createdAt)), escape(t.userEmail),
      escape(t.userName), escape(categoryLabel(t.category)), escape(moduleLabel(t.relatedModule)),
      escape(t.status), escape(t.priority), escape(t.platform), escape(t.appVersion),
      escape(t.message), escape(t.notaGeral), escape(recommendLabel(t.recomendaria)),
      escape(t.principalProblema), escape(t.principalMelhoria), escape(t.internalNote),
      escape(String(derived.filter((p) => !p.resolved).length)),
      escape(String(derived.length)),
    ];
  });
  const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `suporte-beta-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

const AdminSupportBetaPanel: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [filterModule, setFilterModule] = useState<string>("all");
  const [filterPlatform, setFilterPlatform] = useState<string>("all");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [filterSource, setFilterSource] = useState<string>("all");
  const [filterHasProblems, setFilterHasProblems] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const q = query(collection(db, "supportTickets"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q,
      (snapshot) => {
        setTickets(snapshot.docs.map((d) => {
          const raw = d.data();
          // web tickets use description as message fallback
          const message = raw.message || raw.description || "";
          return {
            id: d.id,
            uid: raw.uid ?? null,
            userEmail: raw.userEmail ?? null,
            userName: raw.userName ?? null,
            category: raw.category ?? null,
            relatedModule: raw.relatedModule ?? raw.moduleAffected ?? null,
            message,
            platform: raw.platform ?? null,
            appVersion: raw.appVersion ?? null,
            source: (raw.source as "web" | "mobile") ?? null,
            status: (raw.status as TicketStatus) ?? "open",
            priority: (raw.priority as TicketPriority) ?? null,
            internalNote: raw.internalNote ?? null,
            checklistBeta: raw.checklistBeta ?? null,
            notaGeral: raw.notaGeral ?? null,
            recomendaria: raw.recomendaria ?? null,
            principalProblema: raw.principalProblema ?? null,
            principalMelhoria: raw.principalMelhoria ?? null,
            problems: Array.isArray(raw.problems) ? raw.problems : null,
            createdAt: raw.createdAt ?? null,
            kind: raw.kind ?? null,
            title: raw.title ?? null,
            description: raw.description ?? null,
            returnEmail: raw.returnEmail ?? null,
            moduleAffected: raw.moduleAffected ?? null,
            clinicalErrorType: raw.clinicalErrorType ?? null,
            clinicalImpact: raw.clinicalImpact ?? null,
            impact: raw.impact ?? null,
            expectedFlow: raw.expectedFlow ?? null,
            attachments: Array.isArray(raw.attachments) ? raw.attachments : null,
            context: raw.context ?? null,
          };
        }));
        setLoading(false);
      },
      (err) => {
        console.error("[AdminSupport][snapshot:error]", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [refreshKey]);

  // ─ Counters ─
  const counters = useMemo(() => {
    let problemsPending = 0;
    let problemsFixing = 0;
    let problemsResolved = 0;
    for (const t of tickets) {
      const probs = t.problems?.length ? t.problems : deriveProblems(t);
      for (const p of probs) {
        if (p.resolved || p.status === "resolved") problemsResolved++;
        else if (p.status === "fixing") problemsFixing++;
        else problemsPending++;
      }
    }
    return {
      open: tickets.filter((t) => t.status === "open").length,
      in_review: tickets.filter((t) => t.status === "in_review").length,
      resolved: tickets.filter((t) => t.status === "resolved").length,
      critical: tickets.filter((t) => t.priority === "critical").length,
      problemsPending,
      problemsFixing,
      problemsResolved,
    };
  }, [tickets]);

  // ─ Filtered list ─
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return tickets.filter((t) => {
      if (filterStatus !== "all" && t.status !== filterStatus) return false;
      if (filterCategory !== "all" && t.category !== filterCategory) return false;
      if (filterModule !== "all" && t.relatedModule !== filterModule) return false;
      if (filterPlatform !== "all" && t.platform !== filterPlatform) return false;
      if (filterSource !== "all" && (t.source ?? "mobile") !== filterSource) return false;
      if (filterPriority !== "all" && t.priority !== filterPriority) return false;
      if (filterHasProblems) {
        const probs = t.problems?.length ? t.problems : deriveProblems(t);
        if (!probs.some((p) => !p.resolved && p.status !== "resolved")) return false;
      }
      if (q) {
        const hay = [t.userEmail, t.userName, t.message, t.principalProblema, t.principalMelhoria]
          .join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, search, filterStatus, filterCategory, filterModule, filterPlatform, filterPriority, filterHasProblems]);

  const selectCls =
    "bg-slate-800/80 border border-slate-700/60 text-slate-200 text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-cyan-500/50 cursor-pointer";

  const hasActiveFilters =
    filterStatus !== "all" || filterCategory !== "all" || filterModule !== "all" ||
    filterPlatform !== "all" || filterPriority !== "all" || filterSource !== "all" ||
    filterHasProblems || search;

  function clearFilters() {
    setFilterStatus("all"); setFilterCategory("all"); setFilterModule("all");
    setFilterPlatform("all"); setFilterPriority("all"); setFilterSource("all");
    setFilterHasProblems(false); setSearch("");
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-3" />
        Carregando chamados...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ─ Ticket counters ─ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <CounterCard label="Abertos" count={counters.open} accent="border-cyan-500/20"
          icon={<Inbox className="w-7 h-7 text-cyan-400" />} />
        <CounterCard label="Em análise" count={counters.in_review} accent="border-amber-500/20"
          icon={<Clock className="w-7 h-7 text-amber-400" />} />
        <CounterCard label="Resolvidos" count={counters.resolved} accent="border-emerald-500/20"
          icon={<CheckCircle2 className="w-7 h-7 text-emerald-400" />} />
        <CounterCard label="Críticos" count={counters.critical} accent="border-red-500/20"
          icon={<AlertCircle className="w-7 h-7 text-red-400" />} />
      </div>

      {/* ─ Problem counters ─ */}
      {(counters.problemsPending + counters.problemsFixing + counters.problemsResolved) > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <CounterCard label="Prob. pendentes" count={counters.problemsPending} accent="border-red-500/15"
            icon={<XCircle className="w-6 h-6 text-red-400" />} />
          <CounterCard label="Em correção" count={counters.problemsFixing} accent="border-amber-500/15"
            icon={<Wrench className="w-6 h-6 text-amber-400" />} />
          <CounterCard label="Prob. resolvidos" count={counters.problemsResolved} accent="border-emerald-500/15"
            icon={<Zap className="w-6 h-6 text-emerald-400" />} />
        </div>
      )}

      {/* ─ Toolbar ─ */}
      <div className="rounded-2xl border border-slate-800/70 bg-slate-900/60 px-5 py-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            <input
              type="text" value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por email, nome, mensagem..."
              className="w-full bg-slate-800/80 border border-slate-700/60 text-slate-100 text-sm rounded-xl pl-9 pr-4 py-2 focus:outline-none focus:border-cyan-500/50 placeholder:text-slate-600"
            />
          </div>
          <button onClick={() => setRefreshKey((k) => k + 1)}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" title="Recarregar">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => exportCSV(filtered)}
            className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/25 bg-emerald-500/10 hover:bg-emerald-500/15 px-4 py-2 text-sm font-bold text-emerald-300 transition-colors">
            <Download className="w-4 h-4" />
            Exportar CSV
          </button>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <Filter className="w-3.5 h-3.5 text-slate-500" />
          {/* Quick-filter: problemas pendentes */}
          <button
            onClick={() => setFilterHasProblems((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-bold transition-colors ${
              filterHasProblems
                ? "bg-red-500/15 border-red-500/30 text-red-300"
                : "bg-slate-800/60 border-slate-700/50 text-slate-400 hover:text-slate-200"
            }`}
          >
            <ListChecks className="w-3.5 h-3.5" />
            Com problemas pendentes
          </button>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={selectCls}>
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className={selectCls}>
            {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className={selectCls}>
            {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={filterModule} onChange={(e) => setFilterModule(e.target.value)} className={selectCls}>
            {MODULE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)} className={selectCls}>
            {PLATFORM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className={selectCls}>
            {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {hasActiveFilters && (
            <button onClick={clearFilters}
              className="text-xs text-slate-400 hover:text-white underline underline-offset-2 transition-colors">
              Limpar filtros
            </button>
          )}
        </div>

        <p className="text-[11px] text-slate-500">
          {filtered.length} {filtered.length === 1 ? "chamado" : "chamados"} encontrado{filtered.length !== 1 ? "s" : ""}
          {filtered.length !== tickets.length ? ` de ${tickets.length} total` : ""}
        </p>
      </div>

      {/* ─ Tickets list ─ */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-500 gap-3">
          <MessageSquare className="w-10 h-10 opacity-40" />
          <p className="text-sm">Nenhum chamado encontrado.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((ticket) => (
            <TicketRow key={ticket.id} ticket={ticket} onUpdated={() => setRefreshKey((k) => k + 1)} />
          ))}
        </div>
      )}

      {tickets.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center py-20 text-slate-500 gap-3">
          <AlertTriangle className="w-10 h-10 opacity-40" />
          <p className="text-sm">Nenhum chamado recebido ainda.</p>
          <p className="text-xs text-slate-600">Os chamados enviados pelo app mobile aparecerão aqui.</p>
        </div>
      )}
    </div>
  );
};

export default AdminSupportBetaPanel;
